import { useMutation, useQueryClient } from '@tanstack/react-query'
import { createClient } from '@/lib/supabase/client'
import type { Database, Json } from '@/types/database'

type Tables = Database['public']['Tables']

// Row types for typed arrays
type SeriesRow = Tables['series']['Row']
type EpisodeRow = Tables['episodes']['Row']
type GuestRow = Tables['guests']['Row']
type AppearanceRow = Tables['appearances']['Row']
type CommunicationRow = Tables['communications']['Row']
type PrepChecklistRow = Tables['prep_checklists']['Row']
type PostProductionRow = Tables['post_production']['Row']
type ProductionSettingsRow = Tables['production_settings']['Row']

// Export types (without team_id)
interface SeriesExport {
  id: string
  name: string
  description: string | null
  series_type: 'podcast' | 'channel' | 'show'
  cover_image_url: string | null
  created_at: string
  updated_at: string
}

interface EpisodeExport {
  id: string
  series_id: string
  number: number
  title: string
  description: string | null
  recording_date: string
  recording_time: string | null
  air_date: string
  status: 'scheduled' | 'recorded' | 'in-production' | 'published' | 'cancelled'
  production_lead_days: number
  thumbnail_url: string | null
  duration: string | null
  listens: number | null
  notes: string | null
  platform: string | null
  meeting_link: string | null
  created_at: string
  updated_at: string
}

interface GuestExport {
  id: string
  name: string
  title: string
  company: string | null
  avatar_url: string | null
  email: string | null
  phone: string | null
  location: string | null
  bio: string | null
  social_links: Json | null
  tags: string[] | null
  status: 'active' | 'archived'
  notes: string | null
  created_at: string
  updated_at: string
}

interface ProductionSettingsExport {
  id: string
  default_lead_days: number
  recording_buffer_days: number
  max_episodes_per_day: number
  updated_at: string
}

/**
 * Export data structure containing all team data
 */
export interface TeamDataExport {
  version: string
  exportedAt: string
  team: {
    name: string
  }
  series: SeriesExport[]
  episodes: EpisodeExport[]
  guests: GuestExport[]
  appearances: AppearanceRow[]
  communications: CommunicationRow[]
  prepChecklists: PrepChecklistRow[]
  postProduction: PostProductionRow[]
  productionSettings: ProductionSettingsExport | null
}

/**
 * Hook to export all team data as JSON
 */
export function useDataExport() {
  const supabase = createClient()

  return useMutation({
    mutationFn: async (teamId: string): Promise<TeamDataExport> => {
      // Fetch team info
      const { data: team, error: teamError } = await supabase
        .from('teams')
        .select('name')
        .eq('id', teamId)
        .single()

      if (teamError) throw new Error(`Failed to fetch team: ${teamError.message}`)
      const teamData = team as { name: string }

      // Fetch all team data in parallel
      const [seriesResult, episodesResult, guestsResult, productionSettingsResult] =
        await Promise.all([
          supabase.from('series').select('*').eq('team_id', teamId),
          supabase.from('episodes').select('*').eq('team_id', teamId),
          supabase.from('guests').select('*').eq('team_id', teamId),
          supabase.from('production_settings').select('*').eq('team_id', teamId).maybeSingle(),
        ])

      if (seriesResult.error)
        throw new Error(`Failed to fetch series: ${seriesResult.error.message}`)
      if (episodesResult.error)
        throw new Error(`Failed to fetch episodes: ${episodesResult.error.message}`)
      if (guestsResult.error)
        throw new Error(`Failed to fetch guests: ${guestsResult.error.message}`)
      if (productionSettingsResult.error)
        throw new Error(
          `Failed to fetch production settings: ${productionSettingsResult.error.message}`
        )

      const series = (seriesResult.data || []) as SeriesRow[]
      const episodes = (episodesResult.data || []) as EpisodeRow[]
      const guests = (guestsResult.data || []) as GuestRow[]
      const productionSettings = productionSettingsResult.data as ProductionSettingsRow | null

      // Get IDs for related data queries
      const episodeIds = episodes.map((e) => e.id)
      const guestIds = guests.map((g) => g.id)

      // Fetch related data in parallel
      const [appearancesResult, communicationsResult] = await Promise.all([
        episodeIds.length > 0
          ? supabase.from('appearances').select('*').in('episode_id', episodeIds)
          : Promise.resolve({ data: [] as AppearanceRow[], error: null }),
        guestIds.length > 0
          ? supabase.from('communications').select('*').in('guest_id', guestIds)
          : Promise.resolve({ data: [] as CommunicationRow[], error: null }),
      ])

      if (appearancesResult.error)
        throw new Error(`Failed to fetch appearances: ${appearancesResult.error.message}`)
      if (communicationsResult.error)
        throw new Error(`Failed to fetch communications: ${communicationsResult.error.message}`)

      const appearances = (appearancesResult.data || []) as AppearanceRow[]
      const communications = (communicationsResult.data || []) as CommunicationRow[]

      // Get appearance IDs for checklists
      const appearanceIds = appearances.map((a) => a.id)

      // Fetch prep checklists and post production
      const [prepChecklistsResult, postProductionResult] = await Promise.all([
        appearanceIds.length > 0
          ? supabase.from('prep_checklists').select('*').in('appearance_id', appearanceIds)
          : Promise.resolve({ data: [] as PrepChecklistRow[], error: null }),
        episodeIds.length > 0
          ? supabase.from('post_production').select('*').in('episode_id', episodeIds)
          : Promise.resolve({ data: [] as PostProductionRow[], error: null }),
      ])

      if (prepChecklistsResult.error)
        throw new Error(`Failed to fetch prep checklists: ${prepChecklistsResult.error.message}`)
      if (postProductionResult.error)
        throw new Error(`Failed to fetch post production: ${postProductionResult.error.message}`)

      const prepChecklists = (prepChecklistsResult.data || []) as PrepChecklistRow[]
      const postProduction = (postProductionResult.data || []) as PostProductionRow[]

      // Remove team_id from exported data (will be assigned to importing team)
      const exportData: TeamDataExport = {
        version: '1.0',
        exportedAt: new Date().toISOString(),
        team: {
          name: teamData.name,
        },
        series: series.map((s) => ({
          id: s.id,
          name: s.name,
          description: s.description,
          series_type: s.series_type,
          cover_image_url: s.cover_image_url,
          created_at: s.created_at,
          updated_at: s.updated_at,
        })),
        episodes: episodes.map((e) => ({
          id: e.id,
          series_id: e.series_id,
          number: e.number,
          title: e.title,
          description: e.description,
          recording_date: e.recording_date,
          recording_time: e.recording_time,
          air_date: e.air_date,
          status: e.status,
          production_lead_days: e.production_lead_days,
          thumbnail_url: e.thumbnail_url,
          duration: e.duration,
          listens: e.listens,
          notes: e.notes,
          platform: e.platform,
          meeting_link: e.meeting_link,
          created_at: e.created_at,
          updated_at: e.updated_at,
        })),
        guests: guests.map((g) => ({
          id: g.id,
          name: g.name,
          title: g.title,
          company: g.company,
          avatar_url: g.avatar_url,
          email: g.email,
          phone: g.phone,
          location: g.location,
          bio: g.bio,
          social_links: g.social_links,
          tags: g.tags,
          status: g.status,
          notes: g.notes,
          created_at: g.created_at,
          updated_at: g.updated_at,
        })),
        appearances,
        communications,
        prepChecklists,
        postProduction,
        productionSettings: productionSettings
          ? {
              id: productionSettings.id,
              default_lead_days: productionSettings.default_lead_days,
              recording_buffer_days: productionSettings.recording_buffer_days,
              max_episodes_per_day: productionSettings.max_episodes_per_day,
              updated_at: productionSettings.updated_at,
            }
          : null,
      }

      return exportData
    },
  })
}

/**
 * Download data as JSON file
 */
export function downloadAsJson(data: TeamDataExport, filename: string) {
  const jsonString = JSON.stringify(data, null, 2)
  const blob = new Blob([jsonString], { type: 'application/json' })
  const url = URL.createObjectURL(blob)
  const link = document.createElement('a')
  link.href = url
  link.download = filename
  document.body.appendChild(link)
  link.click()
  document.body.removeChild(link)
  URL.revokeObjectURL(url)
}

/**
 * Validate import data structure
 */
export function validateImportData(data: unknown): data is TeamDataExport {
  if (!data || typeof data !== 'object') return false

  const obj = data as Record<string, unknown>

  return (
    typeof obj.version === 'string' &&
    typeof obj.exportedAt === 'string' &&
    typeof obj.team === 'object' &&
    obj.team !== null &&
    Array.isArray(obj.series) &&
    Array.isArray(obj.episodes) &&
    Array.isArray(obj.guests) &&
    Array.isArray(obj.appearances) &&
    Array.isArray(obj.communications) &&
    Array.isArray(obj.prepChecklists) &&
    Array.isArray(obj.postProduction)
  )
}

/**
 * Import result statistics
 */
export interface ImportResult {
  seriesImported: number
  episodesImported: number
  guestsImported: number
  appearancesImported: number
  communicationsImported: number
  prepChecklistsImported: number
  postProductionImported: number
}

/**
 * Hook to import team data from JSON
 */
export function useDataImport() {
  const supabase = createClient()
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({
      teamId,
      data,
    }: {
      teamId: string
      data: TeamDataExport
    }): Promise<ImportResult> => {
      // Validate data
      if (!validateImportData(data)) {
        throw new Error('Invalid import data format')
      }

      // Create ID maps for linking related records
      const seriesIdMap = new Map<string, string>()
      const episodeIdMap = new Map<string, string>()
      const guestIdMap = new Map<string, string>()
      const appearanceIdMap = new Map<string, string>()

      // Import series
      for (const series of data.series) {
        const oldId = series.id
        const { data: newSeries, error } = await supabase
          .from('series')
          .insert({
            team_id: teamId,
            name: series.name,
            description: series.description,
            series_type: series.series_type,
            cover_image_url: series.cover_image_url,
          } as never)
          .select('id')
          .single()

        if (error) throw new Error(`Failed to import series: ${error.message}`)
        seriesIdMap.set(oldId, (newSeries as { id: string }).id)
      }

      // Import guests
      for (const guest of data.guests) {
        const oldId = guest.id
        const { data: newGuest, error } = await supabase
          .from('guests')
          .insert({
            team_id: teamId,
            name: guest.name,
            title: guest.title,
            company: guest.company,
            email: guest.email,
            phone: guest.phone,
            location: guest.location,
            bio: guest.bio,
            avatar_url: guest.avatar_url,
            social_links: guest.social_links,
            tags: guest.tags,
            status: guest.status,
            notes: guest.notes,
          } as never)
          .select('id')
          .single()

        if (error) throw new Error(`Failed to import guest: ${error.message}`)
        guestIdMap.set(oldId, (newGuest as { id: string }).id)
      }

      // Import episodes (mapping series_id)
      for (const episode of data.episodes) {
        const oldId = episode.id
        const newSeriesId = seriesIdMap.get(episode.series_id)
        if (!newSeriesId) {
          console.warn(`Series ${episode.series_id} not found for episode, skipping`)
          continue
        }

        const { data: newEpisode, error } = await supabase
          .from('episodes')
          .insert({
            team_id: teamId,
            series_id: newSeriesId,
            number: episode.number,
            title: episode.title,
            description: episode.description,
            recording_date: episode.recording_date,
            recording_time: episode.recording_time,
            air_date: episode.air_date,
            status: episode.status,
            production_lead_days: episode.production_lead_days,
            thumbnail_url: episode.thumbnail_url,
            duration: episode.duration,
            listens: episode.listens,
            notes: episode.notes,
            platform: episode.platform,
            meeting_link: episode.meeting_link,
          } as never)
          .select('id')
          .single()

        if (error) throw new Error(`Failed to import episode: ${error.message}`)
        episodeIdMap.set(oldId, (newEpisode as { id: string }).id)
      }

      // Import appearances (mapping guest_id and episode_id)
      for (const appearance of data.appearances) {
        const oldId = appearance.id
        const newGuestId = guestIdMap.get(appearance.guest_id)
        const newEpisodeId = episodeIdMap.get(appearance.episode_id)

        if (!newGuestId || !newEpisodeId) {
          console.warn(`Guest or episode not found for appearance, skipping`)
          continue
        }

        const { data: newAppearance, error } = await supabase
          .from('appearances')
          .insert({
            guest_id: newGuestId,
            episode_id: newEpisodeId,
            stage: appearance.stage,
            ready_to_record: appearance.ready_to_record,
            declined: appearance.declined,
            note: appearance.note,
          } as never)
          .select('id')
          .single()

        if (error) throw new Error(`Failed to import appearance: ${error.message}`)
        appearanceIdMap.set(oldId, (newAppearance as { id: string }).id)
      }

      // Import communications (mapping guest_id and appearance_id)
      let communicationsImported = 0
      for (const comm of data.communications) {
        const newGuestId = guestIdMap.get(comm.guest_id)

        if (!newGuestId) {
          console.warn(`Guest not found for communication, skipping`)
          continue
        }

        const newAppearanceId = comm.appearance_id
          ? appearanceIdMap.get(comm.appearance_id)
          : null

        const { error } = await supabase.from('communications').insert({
          guest_id: newGuestId,
          appearance_id: newAppearanceId,
          type: comm.type,
          subject: comm.subject,
          content: comm.content,
          direction: comm.direction,
          sent_by: comm.sent_by,
        } as never)

        if (error) {
          console.warn(`Failed to import communication: ${error.message}`)
          continue
        }
        communicationsImported++
      }

      // Import prep checklists (mapping appearance_id)
      let prepChecklistsImported = 0
      for (const checklist of data.prepChecklists) {
        const newAppearanceId = appearanceIdMap.get(checklist.appearance_id)

        if (!newAppearanceId) {
          console.warn(`Appearance not found for prep checklist, skipping`)
          continue
        }

        const { error } = await supabase.from('prep_checklists').insert({
          appearance_id: newAppearanceId,
          research_complete: checklist.research_complete,
          questions_ready: checklist.questions_ready,
          guest_briefed: checklist.guest_briefed,
          equipment_tested: checklist.equipment_tested,
        } as never)

        if (error) {
          console.warn(`Failed to import prep checklist: ${error.message}`)
          continue
        }
        prepChecklistsImported++
      }

      // Import post production (mapping episode_id)
      let postProductionImported = 0
      for (const postProd of data.postProduction) {
        const newEpisodeId = episodeIdMap.get(postProd.episode_id)

        if (!newEpisodeId) {
          console.warn(`Episode not found for post production, skipping`)
          continue
        }

        const { error } = await supabase.from('post_production').insert({
          episode_id: newEpisodeId,
          editing: postProd.editing,
          transcription: postProd.transcription,
          show_notes: postProd.show_notes,
          artwork: postProd.artwork,
        } as never)

        if (error) {
          console.warn(`Failed to import post production: ${error.message}`)
          continue
        }
        postProductionImported++
      }

      // Import production settings
      if (data.productionSettings) {
        await supabase
          .from('production_settings')
          .upsert(
            {
              team_id: teamId,
              default_lead_days: data.productionSettings.default_lead_days,
              recording_buffer_days: data.productionSettings.recording_buffer_days,
              max_episodes_per_day: data.productionSettings.max_episodes_per_day,
            } as never,
            { onConflict: 'team_id' }
          )
      }

      return {
        seriesImported: seriesIdMap.size,
        episodesImported: episodeIdMap.size,
        guestsImported: guestIdMap.size,
        appearancesImported: appearanceIdMap.size,
        communicationsImported,
        prepChecklistsImported,
        postProductionImported,
      }
    },
    onSuccess: () => {
      // Invalidate all relevant queries
      queryClient.invalidateQueries({ queryKey: ['series'] })
      queryClient.invalidateQueries({ queryKey: ['episodes'] })
      queryClient.invalidateQueries({ queryKey: ['guests'] })
      queryClient.invalidateQueries({ queryKey: ['appearances'] })
    },
  })
}

/**
 * Read and parse a JSON file
 */
export function readJsonFile(file: File): Promise<unknown> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onload = (e) => {
      try {
        const data = JSON.parse(e.target?.result as string)
        resolve(data)
      } catch {
        reject(new Error('Invalid JSON file'))
      }
    }
    reader.onerror = () => reject(new Error('Failed to read file'))
    reader.readAsText(file)
  })
}
